import React from 'react';
import PropTypes from 'prop-types';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import { useSnackbarContext } from 'Context/SnackbarContext';
import useHomeContext from 'Context/HomeContext';
import fr from 'facet-rest';
import { QueryVal } from 'Helpers/Query';
import { navigateTo } from 'Helpers/Redirect';

const SetNewPasswordContext = React.createContext(undefined);

// Actions
export const INITIALIZE_STARTED = 'initialize-started';
export const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
export const INITIALIZE_FAILED = 'initialize-failed';

export const UPDATE_COLLECTION = 'update-collection';

const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    isSubmitting: false,
    submittingError: null,
    authCollection: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        authCollection: action.authCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case UPDATE_COLLECTION: {
      return {
        ...state,
        authCollection: action.authCollection,
      };
    }
    default: {
      throw new Error(`Unhandled SetNewPasswordContext action type: ${action.type}`);
    }
  }
};

const SetNewPasswordProvider = ({
  children,
}) => {
  const { homeResource } = useHomeContext();
  const { displayErrorMessage } = useSnackbarContext();

  const [state, dispatch] = React.useReducer(reducer, null, init);

  const token = QueryVal('t');

  const handleSubmit = React.useCallback(async (coll) => {
    dispatch({ type: UPDATE_COLLECTION, authCollection: coll });
  }, [state]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      isSubmitting: state.isSubmitting,
      submittingError: state.submittingError,
      authCollection: state.authCollection,
      handleSubmit,
    };
  }, [state, handleSubmit]);

  useComponentDidMount(async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });

      let coll;
      if (homeResource.relationship(fr.relationships.user.name)) {
        // If we have a user relationship, use that first
        coll = await homeResource.relationship(fr.relationships.user.name).link('related').get();
      } else if (token) {
        // Use the token to render the set password resource
        try {
          const [setNewPasswordResource] = await homeResource.relationship(fr.relationships.setNewPassword.name).link('related').get();
          setNewPasswordResource.attr('token', token);
          coll = await setNewPasswordResource.submit();
        } catch (err) {
          // bail to the password reset screen if the token is bad
          displayErrorMessage(err, err.errors);
          navigateTo('/change-password');
        }
      } else {
        coll = await homeResource.relationship(fr.relationships.resetPassword.name).link('related').get();
      }

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        authCollection: coll,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
      displayErrorMessage(err, err.errors);
    }
  });

  return (
    <SetNewPasswordContext.Provider value={memoizedValue}>
      {children}
    </SetNewPasswordContext.Provider>
  );
};

// Use Survey State Context
const useSetNewPasswordContext = () => {
  const context = React.useContext(SetNewPasswordContext);
  if (context === undefined) {
    throw new Error('useSetNewPasswordContext must be used within a SetNewPasswordProvider');
  }
  return context;
};

SetNewPasswordProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  SetNewPasswordProvider,
  useSetNewPasswordContext,
};
